import type {
  MeasureDescriptor,
  MeasureGroup,
  MeasuresStructure,
  MeasureSubgroup,
} from "../../../../../../../../../api/types/svTypes.ts";
import { getGroupSubgroups } from "../../customMeasure/utilities/measureSubgroups";

export interface MeasureTreeSubgroup {
  id: string;
  caption: string;
  names: string[];
}

export interface MeasureTreeGroup {
  caption: string;
  names: string[];
  groups: MeasureTreeSubgroup[];
}

type MeasureSubgroupReference = Pick<MeasureDescriptor, "name" | "group" | "subgroupId">;

const buildTreeGroup = (
  group: MeasureGroup,
  subgroupsOfGroup: MeasureSubgroup[],
  subgroupIdByMeasureName: Map<string, string | undefined>
): MeasureTreeGroup => {
  const namesBySubgroupId = new Map<string, string[]>(subgroupsOfGroup.map((subgroup) => [subgroup.id, []]));
  const directNames: string[] = [];

  group.names.forEach((name) => {
    const subgroupId = subgroupIdByMeasureName.get(name);
    const subgroupNames = subgroupId === undefined ? undefined : namesBySubgroupId.get(subgroupId);
    if (subgroupNames) {
      subgroupNames.push(name);
    } else {
      directNames.push(name);
    }
  });

  return {
    caption: group.caption,
    names: directNames,
    groups: subgroupsOfGroup.map((subgroup) => ({
      id: subgroup.id,
      caption: subgroup.name,
      names: namesBySubgroupId.get(subgroup.id) ?? [],
    })),
  };
};

export const buildMeasureSubgroupTree = (
  structure: MeasuresStructure,
  measureSubgroups: MeasureSubgroup[],
  measures: MeasureSubgroupReference[]
): MeasureTreeGroup[] => {
  const parentCaptions = new Set(structure.groups.map((group) => group.caption));
  const subgroupIdByMeasureName = new Map(
    measures.map((measure) => [measure.name, parentCaptions.has(measure.group) ? measure.subgroupId : undefined])
  );
  const existingNames = new Set(structure.groups.flatMap((group) => group.names));
  const fallbackNames = measures
    .filter((measure) => !parentCaptions.has(measure.group) && !existingNames.has(measure.name))
    .map((measure) => measure.name);

  return structure.groups.map((group) => {
    const subgroups = getGroupSubgroups(measureSubgroups, group.caption);
    const names = group.caption === "General" ? [...new Set([...group.names, ...fallbackNames])] : group.names;
    return buildTreeGroup({ ...group, names }, subgroups, subgroupIdByMeasureName);
  });
};

const filterVisibleNames = (names: string[], visibleNames: ReadonlySet<string>): string[] =>
  names.filter((name) => visibleNames.has(name));

export const getVisibleMeasureTreeGroups = (
  groups: MeasureTreeGroup[],
  visibleNames: ReadonlySet<string>,
  showEmptySubgroups: boolean
): MeasureTreeGroup[] =>
  groups
    .map((group) => ({
      caption: group.caption,
      names: filterVisibleNames(group.names, visibleNames),
      groups: group.groups
        .map((subgroup) => ({ ...subgroup, names: filterVisibleNames(subgroup.names, visibleNames) }))
        .filter((subgroup) => subgroup.names.length > 0 || showEmptySubgroups),
    }))
    .filter((group) => group.names.length > 0 || group.groups.length > 0);
